import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateSWP, formatCurrency } from "../../utils/calculations";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import ProductLayout from "@/common/components/ProductLayout";

export const SWPCalculator = () => {
  const navigate = useNavigate();
  const [swpInvestment, setSwpInvestment] = useState(1000000);
  const [swpWithdrawal, setSwpWithdrawal] = useState(5000);
  const [swpRate, setSwpRate] = useState(10);
  const [swpYears, setSwpYears] = useState(10);

  const swpResult = useMemo(
    () => calculateSWP(swpInvestment, swpWithdrawal, swpRate, swpYears),
    [swpInvestment, swpWithdrawal, swpRate, swpYears]
  );

  const swpPieData = [
    { name: "Invested", value: swpInvestment, color: "#e2e8f0" },
    { name: "Returns", value: swpResult.returns, color: "#10b981" },
  ];

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                SWP Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-green-100 p-3">
                      <ArrowUpRight className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        SWP Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Systematic Withdrawal Plan
                      </p>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Total Investment"
                      value={swpInvestment}
                      onChange={setSwpInvestment}
                      min={100000}
                      max={10000000}
                      step={5000}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Monthly Withdrawal"
                      value={swpWithdrawal}
                      onChange={setSwpWithdrawal}
                      min={500}
                      max={100000}
                      step={500}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Expected Return (p.a)"
                      value={swpRate}
                      onChange={setSwpRate}
                      min={1}
                      max={30}
                      step={0.1}
                      suffix="%"
                      colorClass="text-green-600"
                    />

                    <CompactCalculatorRow
                      label="Time Period"
                      value={swpYears}
                      onChange={setSwpYears}
                      min={1}
                      max={30}
                      step={1}
                      suffix="Yrs"
                      colorClass="text-blue-600"
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-muted/20">
                <CardContent className="p-8">
                  <div className="relative mb-6 grid grid-cols-2 gap-4">
                    <div className="col-span-2 flex h-[180px] w-full justify-center">
                      <ResponsiveContainer width={200} height="100%">
                        <PieChart>
                          <Pie
                            data={swpPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            dataKey="value"
                            stroke="none"
                          >
                            {swpPieData.map(e => (
                              <Cell key={e.name} fill={e.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(v: number | undefined) =>
                              v !== undefined ? formatCurrency(v) : ""
                            }
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="col-span-2 space-y-4">
                      <div className="flex items-center justify-between border-b border-slate-100 py-4">
                        <span className="font-medium text-muted-foreground">
                          Total Investment
                        </span>
                        <span className="text-xl font-bold text-secondary">
                          {formatCurrency(swpInvestment)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b border-slate-100 py-4">
                        <span className="font-medium text-muted-foreground">
                          Total Withdrawn
                        </span>
                        <span className="text-xl font-bold text-green-600">
                          {formatCurrency(swpResult.totalWithdrawal)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b border-slate-100 py-4">
                        <span className="font-medium text-muted-foreground">
                          Final Value
                        </span>
                        <span className="text-xl font-bold text-primary">
                          {formatCurrency(swpResult.finalValue)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
